import ExportDropdown, {
  ExportOption,
} from "@/components/design-system/export-dropdown";
import ListHeader from "@/components/design-system/list-header";
import ListPagination from "@/components/design-system/list-pagination";
import {
  Table,
  type TableColumn,
  type TableSortConfig,
} from "@/components/design-system/table";
import {
  deleteAssignment,
  fetchAssignmentById,
  fetchAssignments,
  selectAssignmentActionLoading,
  selectAssignmentLoading,
  selectAssignments,
  selectNoAssignments,
  selectTotalItems,
} from "@/store/slices/assignmentSlice";
import { AppDispatch } from "@store";
import { ExternalLink, Eye, SquarePen, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import EditAssignmentModal from "./edit-assignment-modal";
import ViewAssignmentModal from "./view-assignment-modal";

// Using the SchoolModel from types
import { Badge } from "@/components/design-system/badge";
import { Button } from "@/components/design-system/button";
import FilterWithRadio from "@/components/design-system/FilterWithRadio";
import StudentTypeSelect from "@/components/design-system/StudentTypeSelect";
import Modal from "@/components/design-system/Modal";
import { downloadAssignmentService } from "@/service/assignment.service";
import {
  getAssignResourceDropdownV2Service,
  mapCsListDropdownToGradeOptions,
} from "@/service/base.service";
import { selectUser, selectUserRole } from "@/store/slices/authSlice";
import {
  AssignmentFormData,
  AssignmentModel,
  DownloadAssignmentQuery,
} from "@/types/assignment.model";
import {
  capitalizeFirstLetter,
  CSVIcon,
  downloadFileFromBlob,
  ExcelIcon,
  FileFormats,
  hasMenuPermission,
  hasRolePermissions,
  PDFIcon,
  ROLE_PERMISSIONS,
  successToast,
} from "@/utils/constants";
import { setNavigationSource } from "@/store/slices/courseSlice";
import {
  getAssignmentDetailPath,
  getAssignmentResourceName,
  toRecordFilterQuery,
} from "@/utils/assignmentRecordParams";
import type { StudentListType } from "@/types/student.model";

// Extended interface for UI display purposes
interface AssignmentDisplay extends AssignmentModel {
  sno?: number;
}

// Removed static schoolsData as we'll fetch from API

const ITEMS_PER_PAGE = 10; // Or any other value you prefer

export default function AssignmentListSection() {
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [assignmentType, setAssignmentType] = useState("general");
  const [selectedListType, setSelectedListType] =
    useState<StudentListType>("all");
  const [resourceFilter, setResourceFilter] = useState("");
  const [resourceOptions, setResourceOptions] = useState<
    { id: string; label: string }[]
  >([]);
  const [sortConfig, setSortConfig] = useState<TableSortConfig | undefined>(
    undefined
  );

  const dispatch = useDispatch<AppDispatch>();
  const assignments = useSelector(selectAssignments);
  const loading = useSelector(selectAssignmentLoading);
  const actionLoading = useSelector(selectAssignmentActionLoading);
  const totalItems = useSelector(selectTotalItems);
  const userRole = useSelector(selectUserRole);
  const user = useSelector(selectUser);
  const noAssignments = useSelector(selectNoAssignments);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<string | null>(
    null
  );

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] =
    useState<AssignmentFormData | null>(null);

  const navigate = useNavigate();

  const navigateToAssignment = (record: AssignmentDisplay) => {
    const path = getAssignmentDetailPath(record);

    if (!path) {
      return;
    }

    dispatch(setNavigationSource("assignment-list"));

    const rowAssignmentType =
      record.assign_type === "UNIT" ? "unit" : "general";

    navigate(path, {
      state: {
        selectedTab: "assignment",
        assignmentType: rowAssignmentType,
        lessonId: record?.lesson?.id || null,
        unitId: record?.unit?.id || null,
      },
    });
  };

  // Define columns based on assignment type
  const getColumns = (): TableColumn<AssignmentDisplay>[] => {
    const baseColumns: TableColumn<AssignmentDisplay>[] = [
      {
        key: "sno",
        title: "S.No",
        dataIndex: "sno",
        minWidth: 68,
        maxWidth: 68,
        render: (_, __, index) => {
          // Calculate S.No based on current page and index
          const sno = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
          return (
            <div className="text-[#1F1F1F] text-[16px] font-medium text-center text-borderBlack">
              {String(sno).padStart(2, "0")}
            </div>
          );
        },
      },
      {
        key: "name",
        title: "Assignment Name",
        dataIndex: "name",
        minWidth: 250,
        maxWidth: 250,
        sortable: true,
        render: (_, record) => (
          <div
            className="text-[#1F1F1F] text-[16px] font-medium"
            style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
          >
            {record?.name}
          </div>
        ),
      },
      {
        key: "no_students",
        title: "No of Students",
        minWidth: 175,
        maxWidth: 175,
        sortable: true,
        render: (_, record) => (
          <div
            className="text-[#1F1F1F] text-[16px] font-medium flex items-center"
            style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
          >
            {typeof record?.students === "number" ? record?.students : "-"}

            {typeof record?.students === "number" && record?.students ? (
              <span
                className="ml-2.5 cursor-pointer"
                onClick={() => navigateToAssignment(record)}
              >
                <ExternalLink className="text-[#5C88C5]" />
              </span>
            ) : (
              <></>
            )}
          </div>
        ),
      },
      {
        key: "grade",
        title: "Course / Class",
        minWidth: 200,
        maxWidth: 240,
        sortable: true,
        render: (_, record) => (
          <div
            className="text-[#1F1F1F] text-[16px] font-medium"
            style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
          >
            {getAssignmentResourceName(record) || "-"}
          </div>
        ),
      },
    ];

    // Add lesson and unit columns after grade column when assignmentType is "unit"
    if (assignmentType === "unit") {
      baseColumns.push(
        {
          key: "lesson",
          title: "Lesson",
          minWidth: 148,
          maxWidth: 148,
          sortable: true,
          render: (_, record) => (
            <div
              className="text-[#1F1F1F] text-[16px] font-medium"
              style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
            >
              {record?.lesson?.name || "-"}
            </div>
          ),
        },
        {
          key: "unit",
          title: "Unit",
          minWidth: 148,
          maxWidth: 148,
          sortable: true,
          render: (_, record) => (
            <div
              className="text-[#1F1F1F] text-[16px] font-medium"
              style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
            >
              {record?.unit?.name || "-"}
            </div>
          ),
        }
      );
    }

    // Add remaining standard columns
    baseColumns.push(
      {
        key: "status",
        title: "Assignment Status",
        minWidth: 214,
        maxWidth: 214,
        sortable: true,
        render: (_, record) => {
          const status =
            typeof record.status === "string"
              ? record.status
              : record.status?.str ?? "";

          return (
            <Badge
              variant={
                status === "NEW"
                  ? "primary"
                  : status === "REVIEWED"
                  ? "warning"
                  : "danger"
              }
            >
              {capitalizeFirstLetter(status)}
            </Badge>
          );
        },
      },
      {
        key: "date_time",
        title: "Date & Time",
        minWidth: 276,
        maxWidth: 276,
        sortable: true,
        render: (_, record) => (
          <div className="text-[#1F1F1F] text-[16px] font-medium">
            {record?.created_date}
          </div>
        ),
      },
      {
        key: "no_submission",
        title: "No. of Submissions",
        minWidth: 215,
        maxWidth: 215,
        sortable: true,
        render: (_, record) => (
          <div className="text-[#1F1F1F] text-[16px] font-medium">
            {record?.submissions}
          </div>
        ),
      },
      {
        key: "no_feedbacks",
        title: "No. of Feedbacks",
        minWidth: 215,
        maxWidth: 215,
        sortable: true,
        render: (_, record) => (
          <div className="text-[#1F1F1F] text-[16px] font-medium">
            {record?.feedbacks}
          </div>
        ),
      }
    );

    // Add actions column
    if (
      hasRolePermissions(userRole?.permissions as string[], [
        ROLE_PERMISSIONS.ACTION_VIEW_ASSIGNMENT,
        ROLE_PERMISSIONS.ACTION_EDIT_ASSIGNMENT,
        ROLE_PERMISSIONS.ACTION_DELETE_ASSIGNMENT,
      ])
    ) {
      baseColumns.push({
        key: "actions",
        title: "Actions",
        minWidth: 150,
        maxWidth: 150,
        render: (_, record) => (
          <div className="flex items-center justify-start space-x-2">
            {hasMenuPermission(
              ROLE_PERMISSIONS.ACTION_VIEW_ASSIGNMENT,
              userRole?.permissions as string[]
            ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="View"
                onClick={() => handleViewAssignment(record?.id)}
              >
                <Eye className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}
            {hasMenuPermission(
              ROLE_PERMISSIONS.ACTION_EDIT_ASSIGNMENT,
              userRole?.permissions as string[]
            ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Edit"
                onClick={() => handleEditAssignment(record?.id)}
              >
                <SquarePen className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}
            {hasMenuPermission(
              ROLE_PERMISSIONS.ACTION_DELETE_ASSIGNMENT,
              userRole?.permissions as string[]
            ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Delete"
                onClick={() => {
                  setAssignmentToDelete(record?.id);
                  setDeleteModalOpen(true);
                }}
              >
                <Trash2 className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}
          </div>
        ),
      });
    }

    return baseColumns;
  };

  const columns = getColumns();

  const handleViewAssignment = async (assignmentId: string) => {
    dispatch(fetchAssignmentById(assignmentId))
      .unwrap()
      .then((assignment) => {
        setSelectedAssignment(assignment);
        setViewModalOpen(true);
      })
      .catch((error) => {
        console.log("Failed to fetch assignment:", error);
      });
  };

  const handleEditAssignment = async (assignmentId: string) => {
    dispatch(fetchAssignmentById(assignmentId))
      .unwrap()
      .then((assignment) => {
        setSelectedAssignment(assignment);
        setEditModalOpen(true);
      })
      .catch((error) => {
        console.log("Failed to fetch assignment:", error);
      });
  };

  // Handle delete school
  const handleDeleteAssignment = () => {
    if (assignmentToDelete) {
      dispatch(deleteAssignment(assignmentToDelete))
        .unwrap()
        .then(() => {
          setDeleteModalOpen(false);
          setAssignmentToDelete(null);
          successToast("Assignment deleted successfully", "");
        })
        .catch((error) => {
          console.log("Failed to delete School:", error);
          // Modal stays open on error
        });
    }
  };

  // Handle export of time entries as Excel or PDF
  const handleExport = async (type: "xl" | "pdf" | "csv") => {
    try {
      // Create query params from current filters
      const queryParams: DownloadAssignmentQuery = {
        search: searchValue || undefined,
        fromDate: fromDate,
        toDate: toDate,
        type: assignmentType as "general" | "unit",
        ...toRecordFilterQuery(selectedListType, resourceFilter),
      };

      const response = await downloadAssignmentService(type, queryParams);

      downloadFileFromBlob(response, type, "assignment_report");

      successToast(
        "Export Successful",
        `Assignments exported as ${FileFormats[type].name} successfully`
      );
    } catch (error) {
      console.error(`Error exporting schools as ${type}:`, error);
    }
  };

  const exportOptions: ExportOption[] = [
    {
      label: "PDF",
      icon: <PDFIcon />,
      action: () => handleExport("pdf"),
    },
    {
      label: "Excel",
      icon: <ExcelIcon />,
      action: () => handleExport("xl"),
    },
    {
      label: "CSV",
      icon: <CSVIcon />,
      action: () => handleExport("csv"),
    },
  ];

  // Handle date range changes from the ListHeader component
  const handleDateRangeChange = (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => {
    setFromDate(startDate);
    setToDate(endDate);
    // Reset to first page when date range changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      // The page change will trigger a fetch
    }
  };

  useEffect(() => {
    if (selectedListType === "all") {
      setResourceOptions([]);
      return;
    }

    const fetchResources = async () => {
      try {
        const { data } = await getAssignResourceDropdownV2Service(
          selectedListType,
          { schoolId: user?.school_id }
        );
        setResourceOptions(
          mapCsListDropdownToGradeOptions(data.data).map((item) => ({
            id: item.id,
            label: item.grade,
          }))
        );
      } catch (error) {
        console.error("Error fetching resources:", error);
        setResourceOptions([]);
      }
    };

    fetchResources();
  }, [selectedListType, user?.school_id]);

  const handleListTypeChange = (type: StudentListType) => {
    setSelectedListType(type);
    setResourceFilter("");
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Handle sorting when column headers are clicked
  const handleSort = (config: TableSortConfig | null) => {
    setSortConfig(config || undefined);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Map column keys to API sorting keys
  const getSortField = (key: string): string => {
    const sortFieldMap: Record<string, string> = {
      name: "NME", // Assignment Name
      no_students: "STND", // No. of students
      grade: "GRDE", // Grade
      lesson: "LSN", // Lesson name
      unit: "UNT", // Unit name
      status: "STS", // Status
      date_time: "CDT", // Created date
      no_submission: "SUBSN", // No. of submissions
      no_feedbacks: "FDBk", // No. of feedbacks
      type: "TYP", // Assignment type
    };

    return sortFieldMap[key] || key;
  };

  // Fetch assignments on component mount and when search, pagination, date range, or sorting changes

  // Fetch assignments on component mount and when search, pagination, date range, or sorting changes
  useEffect(() => {
    // Reset to first page when search value changes
    if (searchValue && currentPage !== 1) {
      setCurrentPage(1);
      return; // The page change will trigger another fetch
    }

    // Prepare sorting parameters if sort config exists
    const sortParams = sortConfig
      ? {
          sortBy: getSortField(sortConfig.key),
        }
      : {};

    // Fetch assignments with all parameters including sorting
    dispatch(
      fetchAssignments({
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        search: searchValue,
        fromDate: fromDate,
        toDate: toDate,
        type: assignmentType as "general" | "unit",
        ...toRecordFilterQuery(selectedListType, resourceFilter),
        ...sortParams,
      })
    );
  }, [
    dispatch,
    currentPage,
    searchValue,
    fromDate,
    toDate,
    assignmentType,
    selectedListType,
    resourceFilter,
    sortConfig,
  ]);

  if (noAssignments) {
    return (
      <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden">
        <div
          className={`flex flex-col h-[calc(100vh-140px)] mb-2 overflow-x-auto justify-center items-center`}
        >
          {loading ? (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                Loading...
              </p>
            </>
          ) : (
            <>
              <p className="text-[#1F1F1F] text-[24px] font-bold mb-[9px]">
                No Assignments Added yet
              </p>

              {hasRolePermissions(userRole?.permissions as string[], [
                ROLE_PERMISSIONS.BUTTON_ADD_ASSIGNMENT,
              ]) && (
                <>
                  <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                    Start by creating your first assignment to students
                  </p>
                  <Button
                    className="w-[195px] h-[48px] bg-[#5C88C5] text-white"
                    onClick={() => navigate("/assignments/add-assignment")}
                  >
                    Add Assignment
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden h-full flex flex-1 flex-col">
      <ListHeader
        title={assignmentType === "general" ? "General List" : "Unit List"}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onDateRangeChange={handleDateRangeChange}
        leftAction={
          <>
            <FilterWithRadio
              className="w-full shrink-0 sm:w-[160px]"
              showAbove={false}
              options={[
                { label: "General", value: "general" },
                { label: "Unit", value: "unit" },
              ]}
              value={assignmentType}
              onChange={setAssignmentType}
            />
            <StudentTypeSelect
              value={selectedListType}
              onChange={handleListTypeChange}
              className="w-full shrink-0 sm:w-[170px]"
            />
            {selectedListType !== "all" && (
              <FilterWithRadio
                className="w-full shrink-0 sm:w-[200px]"
                showAbove={false}
                options={resourceOptions.map((item) => ({
                  label: item.label,
                  value: item.id,
                }))}
                value={resourceFilter}
                onChange={setResourceFilter}
                placeholder={
                  selectedListType === "course" ? "All courses" : "All classes"
                }
              />
            )}
          </>
        }
        rightAction={
          <>
            {hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_EXPORT_ASSIGNMENT,
              userRole?.permissions as string[]
            ) && (
              <ExportDropdown
                options={exportOptions}
                buttonClassName="flex h-[48px] w-full items-center justify-between gap-2 rounded-lg border border-[#5C88C5] px-2 py-[14px] text-[14px] font-semibold text-[#5C88C5] focus:outline-none sm:w-[148px] sm:px-[16px]"
              />
            )}

            {hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_ADD_ASSIGNMENT,
              userRole?.permissions as string[]
            ) && (
              <Button
                variant="primary"
                size="md"
                className="h-[48px] w-full bg-[#5C88C5] text-white sm:w-[161px]"
                onClick={() => navigate("/assignments/add-assignment")}
              >
                Create Assignment
              </Button>
            )}
          </>
        }
      />

      {/* Table Container with fixed header */}
      <div className="flex flex-1 flex-col border border-[#EDEDED] rounded-md overflow-x-auto">
        <div className="flex-1 overflow-auto h-full flex">
          <Table
            columns={columns}
            data={assignments} // Use data from Redux store
            rowKey="id"
            showHeader={true} // Hide header as we're showing it separately above
            size="middle"
            sticky
            scroll={{ x: 1200 }}
            loading={loading} // We're handling loading state ourselves
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        </div>
      </div>

      <ListPagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalItems={totalItems} // Use actual total items from data
        itemsPerPage={ITEMS_PER_PAGE}
        itemsLabel="Assignments"
      />

      {/* View Assignment Modal */}
      <ViewAssignmentModal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        assignment={selectedAssignment}
      />

      {/* Edit Assignment Modal */}
      {selectedAssignment && editModalOpen && (
        <EditAssignmentModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            // Refresh the assignments list after editing
          }}
          onRefresh={() => {
            // Prepare sorting parameters if sort config exists
            const sortParams = sortConfig
              ? {
                  sort_by: getSortField(sortConfig.key),
                  sort_order: sortConfig.direction,
                }
              : {};

            dispatch(
              fetchAssignments({
                page: currentPage,
                pageSize: ITEMS_PER_PAGE,
                search: searchValue,
                fromDate: fromDate,
                toDate: toDate,
                type: assignmentType as "general" | "unit",
                ...toRecordFilterQuery(selectedListType, resourceFilter),
                ...sortParams,
              })
            );
          }}
          assignment={selectedAssignment}
        />
      )}

      {/* Delete School Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Assignment"
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] mb-8">
            Are you sure want to Delete School ?
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() => setDeleteModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={() => handleDeleteAssignment()}
            disabled={actionLoading}
          >
            {actionLoading ? "Deleting..." : "Yes, Delete"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
